import React from 'react'
import LoadingText from '../../ui_reusable/LoadingText'
import useDatasetSummary from './cache_query_handler/useDatasetSummary'
import usePreloadDatasetSummary from './cache_query_handler/usePreloadDatasetSummary'
import {
  WrapWithLineBetween,
  LineCloseToMeElement,
} from '../../ui_reusable/WrapWithLineBetweenDisplayer'

import {
  ExpandedRow,
  InfoDisplayerTitle,
  infoDisplayerValueStyle,
} from '../../ui_reusable/styles/WrapWithLineBetweenDisplayer.styles'

import DatasetGraph from './show_dataset_dropdown/DatasetGraph'
import theme from '../../../styles/theme'
import {
  SimpleInfoContainer,
  decriptionInfoValueStyle,
  SimpleInfoItem,
  GraphStack,
  DescriptionFieldContainer,
} from './styles/ShowDatasetDropdown.styles'

const helpText = {
  season: {
    title: 'Seasonality',
    text: 'Shows recurring patterns across months or seasons, helping identify regular cycles in the time series. It shows increasing or decreasing trends over time by month or season.',
  },

  breaks: {
    title: 'Trend Breaks',
    text: 'Highlights points in time where the underlying trend shifts significantly, suggesting structural changes in the dataset. Such changes may have significant impact on the model prediction, and should be investigated. If the breaks are due to changes in data structure or collection method, you should interpret results with caution.',
  },

  gaps: {
    title: 'Gaps',
    text: 'Identifies gaps in the time series and shows where values have been interpolated to fill in missing data.',
  },
  decompose: {
    title: 'Overview',
    text: 'Displays the full time series with its trend, seasonality, and residual components to highlight the structure and patterns in the data. This is useful when Predict determines the need to extract and model seasonality and trend effects.',
  },
}

const ShowDatasetDropdown = ({
  dataset,
  datasetId,
  units,
  isPreloaded = false,
  isSegment = false,
}) => {
  const datasetQueryInfo = useDatasetSummary(
    isPreloaded ? null : datasetId,
    dataset?.updated_at,
    isSegment,
  )
  const preloadQueryInfo = usePreloadDatasetSummary(
    isPreloaded ? dataset : null,
  )

  // Select the appropriate query based on conditions
  const queryInfo = isPreloaded ? preloadQueryInfo : datasetQueryInfo

  return (
    <ExpandedRow>
      {dataset?.description && (
        <DescriptionFieldContainer>
          <LoadingText css={decriptionInfoValueStyle} isLoading={false}>
            {dataset?.description || 'N/A'}
          </LoadingText>
        </DescriptionFieldContainer>
      )}

      <SimpleInfoContainer
        style={{ backgroundColor: theme.colors.surface, marginTop: '10px' }}
      >
        {dataset?.source && (
          <SimpleInfoItem>
            <InfoDisplayerTitle>Source</InfoDisplayerTitle>
            <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
              {dataset.source}
            </LoadingText>
          </SimpleInfoItem>
        )}
        {dataset?.interval && (
          <SimpleInfoItem>
            <InfoDisplayerTitle>Granularity</InfoDisplayerTitle>
            <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
              {dataset.interval.toUpperCase() || 'N/A'}
            </LoadingText>
          </SimpleInfoItem>
        )}
        {dataset?.updated_at && (
          <SimpleInfoItem>
            <InfoDisplayerTitle>Last updated</InfoDisplayerTitle>
            <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
              {new Date(dataset.updated_at).toISOString().slice(0, 10)}
            </LoadingText>
          </SimpleInfoItem>
        )}

        {dataset?.aggregation_method && (
          <SimpleInfoItem>
            <InfoDisplayerTitle>Aggr. Method</InfoDisplayerTitle>
            <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
              {dataset.aggregation_method || 'N/A'}
            </LoadingText>
          </SimpleInfoItem>
        )}
        {dataset?.units && (
          <SimpleInfoItem>
            <InfoDisplayerTitle>Measure</InfoDisplayerTitle>
            <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
              {dataset.units.toUpperCase() || 'N/A'}
            </LoadingText>
          </SimpleInfoItem>
        )}
        {dataset?.influence && (
          <SimpleInfoItem>
            <InfoDisplayerTitle>Influence</InfoDisplayerTitle>
            <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
              {dataset.influence.toUpperCase() || 'N/A'}
            </LoadingText>
          </SimpleInfoItem>
        )}
        {dataset?.impact && (
          <SimpleInfoItem>
            <InfoDisplayerTitle>Investment</InfoDisplayerTitle>
            <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
              {dataset.impact.toUpperCase() || 'N/A'}
            </LoadingText>
          </SimpleInfoItem>
        )}

        {dataset?.quality && (
          <SimpleInfoItem>
            <InfoDisplayerTitle>Quality</InfoDisplayerTitle>
            <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
              {dataset.quality.toUpperCase() || 'N/A'}
            </LoadingText>
          </SimpleInfoItem>
        )}

        {dataset?.category && (
          <SimpleInfoItem>
            <InfoDisplayerTitle>Category</InfoDisplayerTitle>
            <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
              {dataset.category}
            </LoadingText>
          </SimpleInfoItem>
        )}
      </SimpleInfoContainer>

      <WrapWithLineBetween>
        <LineCloseToMeElement>
          <InfoDisplayerTitle>Total observations</InfoDisplayerTitle>
          <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
            {dataset?.total_observations}
          </LoadingText>
        </LineCloseToMeElement>
        <LineCloseToMeElement>
          <InfoDisplayerTitle>Included observations</InfoDisplayerTitle>
          <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
            {dataset?.included_observations}
          </LoadingText>
        </LineCloseToMeElement>
        <LineCloseToMeElement>
          <InfoDisplayerTitle>Missing</InfoDisplayerTitle>
          <LoadingText css={infoDisplayerValueStyle} isLoading={false}>
            {dataset.total_observations - dataset.included_observations > 0
              ? dataset.total_observations - dataset.included_observations
              : 'None'}
          </LoadingText>
        </LineCloseToMeElement>

        <LineCloseToMeElement>
          <InfoDisplayerTitle>Seasonality</InfoDisplayerTitle>
          <LoadingText
            css={infoDisplayerValueStyle}
            isLoading={queryInfo.isLoading}
          >
            {queryInfo.data?.season > 0 ? 'Detected' : 'Not detected'}
          </LoadingText>
        </LineCloseToMeElement>
        <LineCloseToMeElement>
          <InfoDisplayerTitle>Trend breaks</InfoDisplayerTitle>
          <LoadingText
            css={infoDisplayerValueStyle}
            isLoading={queryInfo.isLoading}
          >
            {queryInfo.data?.breaks ? 'Yes' : 'None'}
          </LoadingText>
        </LineCloseToMeElement>
        <LineCloseToMeElement>
          <InfoDisplayerTitle>Std deviation</InfoDisplayerTitle>
          <LoadingText
            css={infoDisplayerValueStyle}
            isLoading={queryInfo.isLoading}
          >
            {queryInfo.data?.stddev
              ? Number(queryInfo.data.stddev).toFixed(2)
              : queryInfo.data?.stddev}
          </LoadingText>
        </LineCloseToMeElement>
        <LineCloseToMeElement>
          <InfoDisplayerTitle>Mean</InfoDisplayerTitle>
          <LoadingText
            css={infoDisplayerValueStyle}
            isLoading={queryInfo.isLoading}
          >
            {queryInfo.data?.mean
              ? Number(queryInfo.data.mean).toFixed(2)
              : queryInfo.data?.mean}
          </LoadingText>
        </LineCloseToMeElement>
        <LineCloseToMeElement>
          <InfoDisplayerTitle>Smoothing</InfoDisplayerTitle>
          <LoadingText
            css={infoDisplayerValueStyle}
            isLoading={queryInfo.isLoading}
          >
            {dataset.smoothing ? 'Yes' : 'No'}
          </LoadingText>
        </LineCloseToMeElement>
      </WrapWithLineBetween>

      <GraphStack>
        {/* Overview graph is always shown without conditions */}
        <DatasetGraph
          title={helpText.decompose.title}
          helpText={helpText.decompose.text}
          datasetId={datasetId}
          action="decompose"
          dataset={dataset}
          isPreloaded={isPreloaded}
          isSegment={isSegment}
        />

        {/* Always show season graph, but with message if no seasonality */}
        <DatasetGraph
          title={helpText.season.title}
          helpText={helpText.season.text}
          size="small"
          datasetId={datasetId}
          action="season"
          dataset={dataset}
          isPreloaded={isPreloaded}
          isSegment={isSegment}
          overwriteMessage={
            !queryInfo.isLoading &&
            queryInfo.data &&
            !(queryInfo.data.season > 0)
              ? 'No seasonality detected in this dataset'
              : null
          }
        />

        {/* Always show breaks graph, but with message if no breaks */}
        <DatasetGraph
          title={helpText.breaks.title}
          helpText={helpText.breaks.text}
          size="small"
          datasetId={datasetId}
          action="breaks"
          dataset={dataset}
          isPreloaded={isPreloaded}
          isSegment={isSegment}
          overwriteMessage={
            !queryInfo.isLoading && queryInfo.data && !queryInfo.data.breaks
              ? 'No trend breaks detected in this dataset'
              : null
          }
        />

        {/* Always show gaps graph, but with message if no gaps */}
        <DatasetGraph
          size="small"
          title={helpText.gaps.title}
          helpText={helpText.gaps.text}
          datasetId={datasetId}
          action="gaps"
          dataset={dataset}
          isPreloaded={isPreloaded}
          isSegment={isSegment}
          overwriteMessage={
            !queryInfo.isLoading && queryInfo.data && !(queryInfo.data.gaps > 0)
              ? 'No gaps detected in this dataset'
              : null
          }
        />
      </GraphStack>
    </ExpandedRow>
  )
}

export default React.memo(ShowDatasetDropdown)
